import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';

import useMultiRingSelections from '../_hooks/useMultiRingSelections';
import type { BuildHardwareOrderBaseProps } from '../_types/order';
import ModalFooter from './ModalFooter';
import OneYearMembershipSelection from './OneYearMembershipSelection';

interface CurrentContentProps {
  readonly isAddingToCart: boolean;
  readonly isUS: boolean;
  readonly onAddToCart: () => Promise<void>;
  readonly order: BuildHardwareOrderBaseProps['order'];
  readonly ringCount: number;
  readonly onRadioChange: (value: string) => void;
  readonly titleProps: { id?: string | undefined };
  readonly yearlyMembership: BuildHardwareOrderBaseProps['yearlyMembership'];
}

export function CurrentContent({
  isAddingToCart,
  isUS,
  onAddToCart,
  ringCount,
  order,
  onRadioChange,
  titleProps,
  yearlyMembership,
}: CurrentContentProps) {
  const [proceedButtonText, setProceedButtonText] = useState<MessageKey>(
    'bundle_modal_skip_button',
  );
  const { membershipSelection, warrantySelection } = useMultiRingSelections();
  const existingMembershipValue = membershipSelection.value;
  const existingWarrantyValue = warrantySelection.value;
  const selectedWarranty = order.warranty.value as WarrantySelectionEnum;
  const selectedMembership = order.membership.value as MembershipSelectionEnum;
  const isDefaultMultiRingSelection =
    existingMembershipValue === selectedMembership &&
    existingWarrantyValue === selectedWarranty;

  const t = useTranslations();

  // handle multiring footer cta text
  useEffect(() => {
    if (ringCount > 0 && !isAddingToCart) {
      if (isDefaultMultiRingSelection) {
        setProceedButtonText('pdp_add_to_cart');
      } else {
        setProceedButtonText('bundle_cta_update_cart');
      }
    }
  }, [isAddingToCart, isDefaultMultiRingSelection, ringCount]);

  // handle single ring footer cta text and radio change
  const handleRadioChange = (
    value: MembershipSelectionEnum | WarrantySelectionEnum,
  ) => {
    if (ringCount === 0) {
      if (
        (value === MembershipSelectionEnum.OneMonth &&
          selectedWarranty === WarrantySelectionEnum.OneYear) ||
        (value === WarrantySelectionEnum.OneYear &&
          selectedMembership === MembershipSelectionEnum.OneMonth)
      ) {
        setProceedButtonText('bundle_modal_skip_button');
      } else {
        setProceedButtonText('pdp_add_to_cart');
      }
    }
    onRadioChange(value);
  };

  return (
    <div>
      <div className="border-b border-gray-300 pb-6 md:pb-8">
        <Typography
          className="text-2xl font-medium"
          data-cy="bundle_title"
          id={titleProps.id!}
        >
          {t('bundle_modal_title')}
        </Typography>
      </div>
      <div className="grid grid-cols-1 px-2 py-6 md:gap-x-8 xl:gap-x-14">
        <div className="row-start-1">
          <Typography data-cy="one-year-title" height="loose" variant="h6">
            {t('bundle_modal_one_year_title')}
          </Typography>
          <Typography
            className="mb-5"
            data-cy="bundle-membership-subtitle"
            variant="body-small"
          >
            {t(
              isUS
                ? 'bundle_modal_one_year_subtitle_hsa'
                : 'bundle_modal_one_year_subtitle',
            )}
          </Typography>
        </div>
        <OneYearMembershipSelection
          onMembershipSelection={handleRadioChange}
          order={order}
          yearlyMembership={yearlyMembership}
        />
      </div>
      <ModalFooter
        isAddingToCart={isAddingToCart}
        onAddToCart={onAddToCart}
        order={order}
        proceedButtonText={proceedButtonText}
      />
    </div>
  );
}
